import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { m as motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { audiences } from "@/content/audiences";
import { cn } from "@/lib/utils";

/** Interactive segment selector — hover/click an audience to see a live preview of the spår. */
export function AudienceMirror() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(audiences[0].slug);
  const current = audiences.find((a) => a.slug === active)!;

  return (
    <section className="relative py-20 md:py-28">
      <div className="container-wide">
        <div className="mb-10 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] text-peach">Hitta er spegel</div>
          <h2 className="mt-4 text-balance text-4xl font-semibold leading-tight md:text-5xl">
            Klicka. Se er själva.
          </h2>
        </div>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          {/* Spår-väljare */}
          <ul className="space-y-1.5">
            {audiences.map((a) => {
              const isActive = a.slug === active;
              return (
                <li key={a.slug}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(a.slug)}
                    onFocus={() => setActive(a.slug)}
                    onClick={() => setActive(a.slug)}
                    className={cn(
                      "group flex w-full items-center justify-between rounded-2xl border px-5 py-4 text-left transition-all md:px-6 md:py-5",
                      isActive
                        ? "border-peach/40 bg-peach/[0.06]"
                        : "border-white/10 bg-surface/30 hover:border-white/25",
                    )}
                  >
                    <span className="flex items-center gap-4">
                      <span
                        className={cn(
                          "font-mono text-xs",
                          isActive ? "text-peach" : "text-muted-foreground",
                        )}
                      >
                        0{audiences.indexOf(a) + 1}
                      </span>
                      <span
                        className={cn(
                          "text-lg font-semibold transition-colors md:text-xl",
                          isActive ? "text-foreground" : "text-foreground/70",
                        )}
                      >
                        {a.label}
                      </span>
                    </span>
                    <ArrowRight
                      className={cn(
                        "h-4 w-4 transition-transform",
                        isActive ? "text-peach translate-x-0.5" : "text-muted-foreground",
                      )}
                    />
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Spegel-preview */}
          <div className="relative min-h-[420px]">
            <AnimatePresence mode="wait">
              <motion.article
                key={current.slug}
                initial={reduce ? false : { opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: -8 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-[2rem] border border-white/10 bg-surface/60 p-8 backdrop-blur md:p-12"
              >
                <div className="text-xs uppercase tracking-widest text-peach">
                  Spår · {current.label}
                </div>
                <h3 className="mt-4 text-balance text-2xl font-semibold leading-snug md:text-3xl">
                  {current.intro}
                </h3>
                <p className="mt-5 text-pretty text-base text-muted-foreground md:text-lg">
                  {current.longIntro}
                </p>
                <div className="mt-7 rounded-2xl border border-peach/20 bg-peach/[0.05] p-5">
                  <div className="text-[11px] uppercase tracking-widest text-peach">
                    Vad ni får
                  </div>
                  <div className="mt-2 text-base font-medium text-foreground">
                    {current.metric}
                  </div>
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Link
                    to="/for-vem/$slug"
                    params={{ slug: current.slug }}
                    className="group inline-flex items-center gap-2 rounded-full bg-peach px-6 py-3 text-sm font-semibold text-peach-foreground transition-shadow hover:shadow-[0_16px_40px_-12px] hover:shadow-peach/50"
                  >
                    Se hela spåret
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">
                    Vi rekommenderar · <span className="text-foreground/80">{current.recommend}</span>
                  </span>
                </div>
              </motion.article>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
